import {
  type JsonObject,
  type PropertyAttributeType,
  PropertySchemaType,
  zPropertyAttributeType,
} from "~/common/types";
import type { PropertySchemaAssociation_DefaultFragment } from "~/features/admin/propertySchemas";
import {
  type PropertySchemaAssociationFormData,
  type PropertySchemaSetCreateFormData,
  type SelectOption,
  type URLAllowList,
  zSelectOptions,
  zURLAllowList,
} from "~/features/dataImportProjects/types";

export const PropertySchemasMapper = {
  propertyType: {
    validate: (val: string): PropertySchemaType => {
      if (!PropertySchemasMapper.propertyType.isPropertySchemaType(val)) {
        throw new Error(`Invalid property type: ${val}`);
      }
      return val;
    },

    isPropertySchemaType: (val: string): val is PropertySchemaType =>
      Object.values(PropertySchemaType).includes(val as PropertySchemaType),
  },

  selectOption: {
    toFormData: (option: JsonObject): SelectOption[] => {
      const selectOptionsParseRet = zSelectOptions.safeParse(option);
      if (selectOptionsParseRet.success) {
        return selectOptionsParseRet.data;
      }
      return [];
    },
  },

  urlAllowList: {
    toFormData: (urlAllowList: JsonObject | null | undefined): URLAllowList => {
      if (!urlAllowList) {
        return [];
      }

      const parseRet = zURLAllowList.safeParse(urlAllowList);
      if (parseRet.success) {
        return parseRet.data;
      }
      return [];
    },
  },

  propertySchemaAssociation: {
    toFormData: (schema: PropertySchemaAssociation_DefaultFragment): PropertySchemaAssociationFormData => {
      const selectOptions = PropertySchemasMapper.selectOption.toFormData(schema.propertySchema.selectOptions);
      const urlAllowList = PropertySchemasMapper.urlAllowList.toFormData(schema.propertySchema.urlAllowList);
      const propertyTypeId = PropertySchemasMapper.propertyType.validate(schema.propertySchema.propertyTypeId);

      let propertyAttributeTypeId: PropertyAttributeType | undefined;
      if ("propertyAttributeTypeId" in schema) {
        const parseRet = zPropertyAttributeType.safeParse(schema.propertyAttributeTypeId);
        propertyAttributeTypeId = parseRet.success ? parseRet.data : undefined;
      }

      return {
        id: schema.id,
        name: schema.name,
        description: schema.description ?? undefined,
        position: schema.position ?? 0,
        required: schema.required,
        unique: schema.unique,
        propertyAttributeTypeId,
        propertySchema: {
          id: schema.propertySchema.id,
          propertyTypeId,
          selectOptions,
          urlAllowList,
        },
      };
    },

    // copyWithoutId returns a copied PropertySchemaAssociationFormData with the id removed
    copyWithoutId: (schemaAssoc: PropertySchemaAssociationFormData): PropertySchemaAssociationFormData => {
      const { id: _assocId, propertySchema, ...restAssoc } = schemaAssoc;
      const { id: _schemaId, ...restSchema } = propertySchema;

      return {
        id: undefined, // remove id
        ...restAssoc,
        propertySchema: {
          id: undefined, // remove id
          ...restSchema,
        },
      };
    },

    // dropIdIfEmpty returns a copied PropertySchemaAssociationFormData with the id removed if it is empty
    dropIdIfEmpty: (schemaAssoc: PropertySchemaAssociationFormData): PropertySchemaAssociationFormData => {
      const { id: assocId, propertySchema, ...restAssoc } = schemaAssoc;
      const { id: schemaId, ...restSchema } = propertySchema;

      let schemaAssocId = assocId;
      if (schemaAssocId === "") {
        schemaAssocId = undefined;
      }

      let propertySchemaId = schemaId;
      if (propertySchemaId === "") {
        propertySchemaId = undefined;
      }

      return {
        id: schemaAssocId,
        ...restAssoc,
        propertySchema: {
          id: propertySchemaId,
          ...restSchema,
        },
      };
    },
  },

  propertySchemaSet: {
    dropIdIfEmpty: (propertySchemaSet: PropertySchemaSetCreateFormData): PropertySchemaSetCreateFormData => {
      const { id: setId, propertySchemaAssociations } = propertySchemaSet;

      let propertySchemaSetId = setId;
      if (propertySchemaSetId === "") {
        propertySchemaSetId = undefined;
      }

      return {
        id: propertySchemaSetId,
        propertySchemaAssociations: propertySchemaAssociations?.map(
          PropertySchemasMapper.propertySchemaAssociation.dropIdIfEmpty,
        ),
      };
    },
  },
};
